import React, { forwardRef } from 'react';

import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { UrlInlineEditViewIsEditable } from '@atlassian/jira-issue-field-url-inline-edit-full/src/ui/url/index.tsx';
import type { url_nativeIssueTable_UrlCell$key } from '@atlassian/jira-relay/src/__generated__/url_nativeIssueTable_UrlCell.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: url_nativeIssueTable_UrlCell$key;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const UrlCell = ({ fieldRef, isFirstField, hasDuplicates }: Props) => {
	const data = useFragment<url_nativeIssueTable_UrlCell$key>(
		graphql`
			fragment url_nativeIssueTable_UrlCell on JiraUrlField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...url_issueFieldUrlInlineEditFull_UrlInlineEditViewIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	if (
		hasDuplicates &&
		isSingleLineRowHeightEnabled &&
		fg('single_line_row_height_collapsable_fields')
	) {
		return (
			<UrlInlineEditViewIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock={isFirstField ? 'start' : 'center'}
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				readViewFitContainerHeight
				isSimpleInlineSmartLinkEnabled
				shouldRenderHoverPopover={isSingleLineRowHeightEnabled}
			/>
		);
	}

	return (
		<UrlInlineEditViewIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			readViewFitContainerHeight
			isSimpleInlineSmartLinkEnabled
			shouldRenderHoverPopover={isSingleLineRowHeightEnabled}
		/>
	);
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return <Box xcss={customTargetStyles} ref={ref} />;
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	top: 'space.0',
});
